import { AxiosError } from 'axios';
import { flatten } from 'lodash';
import { Reducer} from 'redux';

import {
  AggregationValue,
  AggregationValueWithByCycle,
  MatrixReportAskSegmentAggregation,
  MatrixReportResponseRaw,
  MatrixReportSegmentAggregation,
} from '~src/interfaces/heatmap.interface';
import { ESQuestion, questionToESQuestion } from '~src/interfaces/question.interface';
import {
  CompanySegment,
  Segment,
  SegmentType,
  getEmptyCompanySegment,
} from '~src/interfaces/segment.interface';
import { SurveySubjectTypes } from '~src/interfaces/surveys.interfaces';
import { ESTopic, topicToEsTopic } from '~src/interfaces/topic.interface';
import { AnalysisLine, AnalysisLinePoint } from '~src/modules/spidergraph/AnalysisLine';
import { Survey } from '~src/modules/surveys/interfaces';
import { ThunkSuccessAction } from '~src/services/apiMiddleware';
import logger from '~src/services/logger/LlsLogger';
import {
  RESET_MATRIX,
  fetchQuestionsMatrix,
  fetchRecommendationsTypes,
  fetchTopicsMatrix,
  FetchTopicsMatrixSuccessAction,
  FetchQuestionsMatrixSuccessAction,
  FetchQuestionsMatrixFailureAction,
  FetchTopicsMatrixFailureAction,
  ResetMatrixAction,
  FetchRecommendationsSuccessAction, FetchRecommendationsFailureAction, FetchRecommendationsRequestAction,
} from '~src/store/heatmap/action.types';
import { topRowAggregations } from '~src/store/heatmap/heatmapReducer';
import { unique } from '~src/utils';

import {
  AnalysisActionKeys,
  ChartDimensionsAction
} from './actions';
import {Scale} from "~src/interfaces/scale.interface";

export interface SurveyWithTopics {
  survey: Survey;
  topics: ESTopic[];
}

export interface TopicWithQuestions {
  topic: ESTopic;
  questions: ESQuestion[];
}

export type SpiderChartDimensions = SurveyWithTopics | TopicWithQuestions;

export interface Recommendations {
  text: string;
}

export interface SpiderGraphState {
  dimensions?: SpiderChartDimensions;
  scales?: Scale[];
  lines: AnalysisLine[];
  recommendations?: Recommendations[];
  isLoadingRecommendations?: boolean;
  fetchRecommendationsError?: AxiosError;
}

export function getInitialSpiderGraphState(): SpiderGraphState {
  return {
    lines: [],
  };
}

export function isSurveyWithTopics(dimensions?: SpiderChartDimensions): dimensions is SurveyWithTopics {
  return Boolean(dimensions && (dimensions as SurveyWithTopics).survey);
}
export function isTopicWithQuestions(dimensions?: SpiderChartDimensions): dimensions is TopicWithQuestions {
  return Boolean(dimensions && (dimensions as TopicWithQuestions).topic);
}

type SpidergraphRelatedActions = ChartDimensionsAction
  | FetchTopicsMatrixSuccessAction
  | FetchQuestionsMatrixSuccessAction
  | FetchQuestionsMatrixFailureAction
  | FetchTopicsMatrixFailureAction
  | ResetMatrixAction
  | FetchRecommendationsSuccessAction
  | FetchRecommendationsFailureAction
  | FetchRecommendationsRequestAction;

const spiderGraphReducer: Reducer<SpiderGraphState, SpidergraphRelatedActions> = (
  state = getInitialSpiderGraphState(),
  action: SpidergraphRelatedActions,
): SpiderGraphState => {
  switch (action.type) {
    case fetchTopicsMatrix.Success: {
      const response = (action as ThunkSuccessAction<MatrixReportResponseRaw>).response;
      const newState: SpiderGraphState = { lines: extractLines(response.data, false) };
      const topics = response.data.result.topics;
      if (topics && isSurveyWithTopics(state.dimensions)) {
        newState.dimensions = {
          survey: state.dimensions.survey,
          topics,
        };
      }
      return newState;
    }
    case fetchQuestionsMatrix.Success: {
      const response = (action as ThunkSuccessAction<MatrixReportResponseRaw>).response;
      const newState: SpiderGraphState = { lines: extractLines(response.data, true) };
      const questions = response.data.result.questions;
      if (questions && isTopicWithQuestions(state.dimensions)) {
        newState.dimensions = {
          topic: state.dimensions.topic,
          questions,
        };
      }
      return newState;
    }
    case fetchQuestionsMatrix.Failed:
    case fetchTopicsMatrix.Failed:
      return {
        lines: [],
        dimensions: state.dimensions,
      };
    case RESET_MATRIX: {
      return getInitialSpiderGraphState();
    }

    case AnalysisActionKeys.analysisSurveyTopics: {
      const dimensions = { survey: action.survey, topics: (action.topics || []).map(topicToEsTopic) };
      return {
        ...state,
        dimensions,
      };
    }

    case AnalysisActionKeys.analysisTopicQuestions: {
      const dimensions = { topic: topicToEsTopic(action.topic), questions: (action.questions || []).map(questionToESQuestion) };
      return {
        ...state,
        dimensions,
      };
    }

    case fetchRecommendationsTypes.Request: {
      return {
        lines: [],
        recommendations: [],
        isLoadingRecommendations: true,
        fetchRecommendationsError: undefined,
      };
    }

    case fetchRecommendationsTypes.Failed: {
      return {
        lines: [],
        recommendations: [],
        isLoadingRecommendations: false,
        fetchRecommendationsError: action.error,
      };
    }

    case fetchRecommendationsTypes.Success: {
      const response = (action as ThunkSuccessAction<MatrixReportResponseRaw>).response;

      return {
        lines: [],
        recommendations: response.data.result.recommendations,
        isLoadingRecommendations: false,
      };
    }

    default:
      return state;
  }
};

export const extractLines = (res: MatrixReportResponseRaw, isQuestions: boolean): AnalysisLine[] => {
  let aggregations = res.result.segment_aggregations || [];

  // one limitation that we do have currently: segment aggregation for company will *not* be returned,
  // so cannibalize top row data if it exists, and we can't differentiate cycles there :/
  if (res.result.top_row) {
    const tmp = topRowAggregations(
      res.result.top_row,
      isQuestions,
      res.result.segments.find(s => s.type === SegmentType.Company) as CompanySegment,
    );
    if (tmp) {
      aggregations = aggregations.concat(tmp);
    }
  }

  const askSegmentAggregations = segmentAggregationsToAskSegmentAggregations(
    {
      segment_aggregations: aggregations,
      segment_self_aggregations: res.result.segment_self_aggregations || [],
      ask_segment_aggregations: res.result.ask_segment_aggregations || [],
    },
    res.result.segments.find(s => s.type === SegmentType.Company) as CompanySegment,
    res.options.UrlValues.filterSubject[0],
  );

  return unique(
    flatten(
      askSegmentAggregations.map((segmentAgg): AnalysisLine[] =>
        segmentAskAggregationToAnalysisLines(segmentAgg, res),
      ),
    ),
    (a, b) => a.getLineId() === b.getLineId(),
  );
};

type ToQ = ESTopic | ESQuestion;

function segmentAskAggregationToAnalysisLines(
  askAggregation: MatrixReportAskSegmentAggregation,
  res: MatrixReportResponseRaw,
): AnalysisLine[] {
  const askSegment =
    res.result.segments.find(s => s.id === askAggregation.id) || getEmptyCompanySegment('?');
  return askAggregation.about.flatMap(aboutAggregation =>
    segmentAggregationToAnalysisLines(aboutAggregation, askSegment, res.result.segments, res),
  );
}

function segmentAggregationsToAskSegmentAggregations(
  allAggregations: {
    segment_aggregations: MatrixReportSegmentAggregation[];
    segment_self_aggregations: MatrixReportSegmentAggregation[];
    ask_segment_aggregations: MatrixReportAskSegmentAggregation[];
  },
  company: CompanySegment | undefined,
  surveySubject?: SurveySubjectTypes,
): MatrixReportAskSegmentAggregation[] {
  const companySegmentId = company?.id || '';
  if (!companySegmentId) {
    logger.warn('no company segment in matrix response');
  }
  // if pulse: "about" is the company, segment ids in MatrixReportSegmentAggregation are the "asks"
  // else: "ask" is the company, "about" are  the ids in MatrixReportSegmentAggregation
  let result: MatrixReportAskSegmentAggregation[] = allAggregations.ask_segment_aggregations;
  if (allAggregations.segment_aggregations.length > 0) {
    if (surveySubject === SurveySubjectTypes.General) {
      result = result.concat(
        allAggregations.segment_aggregations.map((sa) => {
          return {
            id: sa.id,
            about: [
              {
                id: companySegmentId,
                questions: sa.questions,
                topics: sa.topics,
              },
            ],
          };
        }),
      );
    }
    if (surveySubject === SurveySubjectTypes.People && result.length === 0) {
      // add company aggregation only if nothing is in ask aggregations
      result.push({
        id: companySegmentId,
        about: allAggregations.segment_aggregations,
      });
    }
  }
  if (allAggregations.segment_self_aggregations.length > 0) {
    result = result.concat(
      allAggregations.segment_self_aggregations.map((selfAggregation) => {
        return {
          id: selfAggregation.id,
          about: [selfAggregation],
        };
      }),
    );
  }

  return result;
}

function segmentAggregationToAnalysisLines(
  segmentAboutAggregation: MatrixReportSegmentAggregation,
  askSegment: Segment,
  segments: Segment[],
  res: MatrixReportResponseRaw,
): AnalysisLine[] {
  const byEdges: AggregationValueWithByCycle[] =
    segmentAboutAggregation.topics || segmentAboutAggregation.questions || [];

  if (!byEdges || !byEdges.length) {
    return [];
  }

  const axisEntities: ToQ[] | undefined = res.result.topics || res.result.questions;
  const cycles = res.result.cycles || [];
  const segmentId = segmentAboutAggregation.id;
  const segment = segments.find(sgm => sgm.id === segmentId) || getEmptyCompanySegment('?');

  const byCycles: Record<string, AggregationValue[]> = {};
  byEdges.forEach((byEdge) => {
    if (byEdge.by_cycle) {
      byEdge.by_cycle.forEach((byCycle) => {
        const cycleId = byCycle.id;
        byCycles[cycleId] = byCycles[cycleId] || [];
        byCycles[cycleId].push({ ...byCycle, id: byEdge.id });
      });
    }
  });

  const lines: AnalysisLine[] = [];
  Object.keys(byCycles).forEach((cycleId) => {
    lines.push(
      new AnalysisLine(
        getPoints(byCycles[cycleId], axisEntities),
        segment,
        askSegment, // company segment would be more correct
        cycles.find(c => c.id === cycleId),
      ),
    );
  });

  if (lines.length !== 1) {
    // dont display average on a single cycle
    lines.push(new AnalysisLine(getPoints(byEdges, axisEntities), segment, askSegment));
  }

  return lines;
}

function getPoints(edges: AggregationValue[], axisEntities?: ToQ[]): AnalysisLinePoint[] {
  return edges.map((edge: AggregationValue) => getPoint(edge, axisEntities));
}

function getPoint(edge: AggregationValue, axisEntities?: ToQ[]): AnalysisLinePoint {
  return {
    edgeId: edge.id,
    value: edge.average,
    numberOfAnswers: edge.number_of_answers,
    axisEntity: axisEntities
      ? axisEntities.find(topicOrQuestion => topicOrQuestion.id === edge.id)
      : undefined,
  };
}

export default spiderGraphReducer;
